// The confirm step for a split somebody has already chosen.
//
// Applying a recommendation used to open the split modal with the condition
// filled in, which asked the user to rebuild a decision they had just made and
// showed none of the figures they made it on. This states the condition and what
// the split would do, in the place the recommendation was read, and saves.
//
// It is not the split form and must not grow into one. Editing a condition is
// what `SplitConditionForm` is for, and the whole value of this path is that
// there is nothing to fill in.
//
// The styles live in `nodeInsights.css` rather than in a file of their own
// because this component has two homes and needs one stylesheet: that file is
// already loaded by the tree page itself, so it is on screen wherever this is.
import React from 'react';
import { ConditionTrait } from '../trees/conditionText';
import { TraitText } from '../trees/NodeTraits';
import {
  formatConversion,
  formatLeads,
  SplitPreview,
  SplitSide,
} from './splitPreview';
import './nodeInsights.css';

interface SplitConfirmProps {
  /** The condition, described the way a node's own condition is. */
  trait: ConditionTrait | null;
  /**
   * The two sides. Null when the recommendation carries no figures, which is
   * what an rgip deliverable cached before those fields existed looks like: the
   * condition is still exact, so the split is still worth offering.
   */
  preview: SplitPreview | null;
  /**
   * Whether a record can carry several values of this attribute, which is every
   * technology and tag field. Membership there is a substring test, so the split can
   * also catch a product whose name contains this one and land a little wide of the
   * figures above. Small, and worth one line rather than a silent difference between
   * the lift somebody read and the lift they got.
   */
  multiValue?: boolean;
  saving: boolean;
  onApply: () => void;
  onCancel: () => void;
}

/**
 * One side of the split. The multiple leads and the counts qualify it, matching
 * the manual panel's recommendation card, because that card is what the reader
 * was looking at a click ago.
 */
function Side({
  side,
  fallbackLabel,
  tone,
}: {
  side: SplitSide;
  fallbackLabel: string;
  tone: 'pos' | 'neg';
}) {
  return (
    <div className={`spc-side spc-side--${tone}`}>
      <div className="spc-side-rule">{side.label ?? fallbackLabel}</div>
      <div className="spc-side-rate">{formatConversion(side.timesAverage)}</div>
      <div className="spc-side-meta">{formatLeads(side)}</div>
    </div>
  );
}

export default function SplitConfirm({
  trait,
  preview,
  multiValue,
  saving,
  onApply,
  onCancel,
}: SplitConfirmProps) {
  return (
    <div className="spc">
      <div className="spc-cond">
        <span className="spc-cond-label">Split on</span>{' '}
        {trait && <TraitText trait={trait} />}
      </div>
      {preview && (
        <div className="spc-sides">
          <Side
            side={preview.matched}
            fallbackLabel="Leads that match"
            tone="pos"
          />
          {preview.other && (
            <Side
              side={preview.other}
              fallbackLabel="Everyone else"
              tone="neg"
            />
          )}
        </div>
      )}
      {multiValue && (
        <p className="spc-note">
          Companies can carry more than one of these, so the split matches the
          name anywhere in the list. It may also catch a product whose name
          contains this one, which can move the figures above a little.
        </p>
      )}
      <div className="spc-actions">
        <button
          type="button"
          className="spc-btn spc-btn--primary"
          onClick={onApply}
          disabled={saving}
        >
          {saving ? 'Creating the split...' : 'Apply'}
        </button>
        <button
          type="button"
          className="spc-btn"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
